import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthProvider';
import { ru } from '../i18n/ru';

export interface AppShellProps {
  title: string;
  subtitle?: string;
  headerRight?: ReactNode;
  children: ReactNode;
}

interface NavItem {
  key: string;
  to: string;
  label: string;
}

/**
 * Каркас панели администратора региона.
 * На широком экране — боковая навигация, на узком (от 360 px) — выдвижное меню;
 * горизонтальной прокрутки нет (ТЗ §4.5).
 */
export function AppShell({ title, subtitle, headerRight, children }: AppShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  const items: NavItem[] = [
    { key: 'analytics', to: '/analytics', label: ru.nav.analytics },
    { key: 'moderation', to: '/moderation', label: ru.nav.moderation },
    { key: 'dictionaries', to: '/dictionaries', label: ru.nav.dictionaries },
    { key: 'roles', to: '/roles', label: ru.nav.roles },
    { key: 'requests', to: '/requests', label: ru.nav.requests },
    { key: 'exports', to: '/exports', label: ru.nav.exports },
    { key: 'registry', to: '/registry', label: ru.nav.registry },
    { key: 'notifications', to: '/notifications', label: ru.nav.notifications },
    { key: 'audit', to: '/audit', label: ru.nav.audit },
  ];

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  const go = (to: string) => {
    setMenuOpen(false);
    navigate(to);
  };

  const navLinkClass = (item: NavItem) =>
    `flex min-h-touch items-center rounded-button px-3 text-sm font-semibold ${
      location.pathname === item.to
        ? 'bg-accent/10 text-accent'
        : 'text-muted hover:text-content'
    }`;

  return (
    <div className="min-h-dvh bg-ink-900 text-content">
      <header className="sticky top-0 z-30 border-b border-line bg-ink-850">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3">
          <div className="min-w-0">
            <h1 className="truncate text-lg font-extrabold">{title}</h1>
            {subtitle ? <p className="truncate text-xs text-muted">{subtitle}</p> : null}
          </div>

          <div className="flex items-center gap-2">
            {headerRight}
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={menuOpen}
              className="min-h-touch rounded-button border border-line px-3 text-sm font-semibold lg:hidden"
            >
              {ru.nav.menu}
            </button>
            <span className="hidden max-w-40 truncate text-xs text-muted lg:inline">
              {user?.displayName ?? user?.email ?? ru.app.panel}
            </span>
            <button
              type="button"
              onClick={() => {
                logout();
                navigate('/login');
              }}
              className="min-h-touch rounded-button border border-line px-3 text-sm font-semibold text-muted"
            >
              {ru.nav.logout}
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-7xl gap-6 px-4 py-5">
        <aside className="hidden w-60 shrink-0 lg:block">
          <nav aria-label={ru.nav.sections} className="sticky top-20 flex flex-col gap-1">
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => go(item.to)}
                aria-current={location.pathname === item.to ? 'page' : undefined}
                className={navLinkClass(item)}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </aside>

        <main className="min-w-0 flex-1 pb-16">{children}</main>
      </div>

      {menuOpen ? (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-ink-900/95 p-4 lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label={ru.nav.sections}
        >
          <div className="mb-4 flex items-center justify-between gap-3">
            <strong className="text-base">{ru.nav.sections}</strong>
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              className="min-h-touch rounded-button border border-line px-4 text-sm font-semibold"
            >
              {ru.nav.close}
            </button>
          </div>

          <nav className="flex flex-col gap-1">
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => go(item.to)}
                aria-current={location.pathname === item.to ? 'page' : undefined}
                className={navLinkClass(item)}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>
      ) : null}
    </div>
  );
}
